import { useState, useMemo } from 'react'
import { Button } from '@/components/ui'
import { useTranslation } from 'react-i18next'
import { UploadCloud, Wrench } from 'lucide-react'
import { useCreateTrolley } from '@/hooks/useToolManagement/useCreateTrolley'
import { useGetTrolleys } from '@/hooks/useToolManagement/useGetTrolleys'
import { useToast } from '@/hooks/useToast'
import type { TrolleySheetData } from '@/utils/tool-management/tool-management.util'
import { filterInvalidRows } from '@/utils/tool-management/row-validation.util'

import { PreviewImportModal } from './PreviewImportModal/PreviewImportModal'
import { ToolImportModal } from './ToolImportModal/ToolImportModal'
import { ToolManagementTable } from './ToolManagementTable/ToolManagementTable'

export function ToolManagement() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isShowModalImport, setIsShowModalImport] = useState<boolean>(false)
  const [isShowPreviewModal, setIsShowPreviewModal] = useState<boolean>(false)
  const [stagedToolData, setStagedToolData] =
    useState<Nullable<TrolleySheetData>>(null)
  const [stagedImportedImages, setStagedImportedImages] = useState<
    {
      name: string
      presignedUrl?: string
      fileKey?: string
      tempKey?: string
      sessionId?: string
    }[]
  >([])
  const { createTrolleys, isLoading: isCreatingTrolleys } = useCreateTrolley()
  const {
    items: trolleys,
    refetch: refetchTrolleys,
    isLoading: isLoadingTrolleys,
  } = useGetTrolleys({
    page: 1,
    page_size: 600,
  })
  const [hadExistingTrolleys, setHadExistingTrolleys] = useState<boolean>(false)

  const toolData = useMemo((): TrolleySheetData => ({}), [])

  const handleImportClick = () => {
    setIsShowModalImport(true)
  }

  const handleImportComplete = (data: {
    file: Nullable<File>
    images: {
      file: File
      preview: string
      name: string
      presignedUrl?: string
      fileKey?: string
      tempKey?: string
      sessionId?: string
    }[]
    data: Nullable<TrolleySheetData>
  }) => {
    setStagedToolData(data.data)
    setStagedImportedImages(
      (data.images || []).map(img => ({
        name: img.name,
        presignedUrl: img.presignedUrl,
        fileKey: img.fileKey,
        tempKey: img.tempKey,
        sessionId: img.sessionId,
      }))
    )
    setHadExistingTrolleys(trolleys.length > 0)
    setIsShowModalImport(false)
    setIsShowPreviewModal(true)
  }

  const handleBackToImport = () => {
    setIsShowPreviewModal(false)
    setIsShowModalImport(true)
  }

  const handleConfirmImport = async () => {
    if (!stagedToolData) return

    const filteredToolData = filterInvalidRows(stagedToolData)

    const imageMeta = stagedImportedImages.map(img => ({
      name: img.name,
      fileKey: img.fileKey ?? null,
      tempKey: img.tempKey ?? null,
      sessionId: img.sessionId ?? null,
    }))

    try {
      await createTrolleys(
        {
          trolleysData: filteredToolData,
          images: imageMeta,
          sessionId: imageMeta[0]?.sessionId ?? null,
        },
        async () => {
          await refetchTrolleys()
          setIsShowPreviewModal(false)
          toast({
            title: hadExistingTrolleys
              ? t('common.importReplaceSuccess')
              : t('common.importSuccess'),
            variant: 'success',
          })
        }
      )
    } catch (error) {
      console.error('Error creating trolleys:', error)
      setIsShowPreviewModal(false)
      setIsShowModalImport(false)
      toast({
        title: t('common.importFailedMessage'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <section className='p-5 overflow-x-hidden w-full max-w-full min-w-0'>
        <header className='flex justify-between items-center border-b pb-4 mb-8'>
          <div className='flex items-center space-x-3'>
            <div className='flex items-center justify-center h-10 w-10 rounded-lg bg-white border border-gray-200 shadow-sm'>
              <Wrench className='h-6 w-6 text-gray-800' />
            </div>
            <div>
              <h1 className='text-xl font-bold text-gray-800'>
                {t('toolManagement.toolManagement')}
              </h1>
              <p className='text-sm text-gray-500'>
                {t('toolManagement.toolManagementDescription')}
              </p>
            </div>
          </div>

          <Button
            type='button'
            className='h-11 font-medium'
            onClick={handleImportClick}
          >
            <UploadCloud className='h-4 w-4 mr-2' />
            {t('common.import')}
          </Button>
        </header>

        {toolData ? (
          <ToolManagementTable
            trolleys={trolleys}
            isLoadingTrolleys={isLoadingTrolleys}
          />
        ) : (
          <div className='flex flex-col items-center justify-center pt-10'>
            <div className='w-full max-w-lg p-10 rounded-2xl text-center transition-all duration-300 bg-white'>
              <img
                src='/empty-tool.png'
                alt='empty-tool'
                className='h-[180px] mx-auto mb-2'
              />

              <h2 className='text-lg font-bold text-[#181D27]'>
                {t('toolManagement.emptyToolList')}
              </h2>

              <p className='text-sm text-[#414651] mt-4'>
                {t('toolManagement.uploadFirstToolList')}
              </p>

              <div className='mt-6 inline-block'>
                <Button
                  type='button'
                  className='h-11 w-[200px] font-medium'
                  onClick={handleImportClick}
                >
                  <UploadCloud className='h-4 w-4 mr-2' />
                  {t('common.import')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>

      <ToolImportModal
        isOpen={isShowModalImport}
        onOpenChange={setIsShowModalImport}
        onImportComplete={handleImportComplete}
      />

      <PreviewImportModal
        isOpen={isShowPreviewModal}
        onOpenChange={setIsShowPreviewModal}
        onBack={handleBackToImport}
        onConfirm={handleConfirmImport}
        onCancel={() => {
          setIsShowPreviewModal(false)
          setIsShowModalImport(false)
        }}
        data={stagedToolData}
        images={stagedImportedImages}
        isConfirming={isCreatingTrolleys}
        hasExistingTrolleys={hadExistingTrolleys}
        onImagesUpdate={updatedImages => {
          setStagedImportedImages(updatedImages)
        }}
      />
    </>
  )
}
